/**
 * GitHub-style identicon generator with gradient backgrounds
 * Generates a 5x5 symmetric pixel pattern based on email hash
 */

interface Palette {
  fg: string;
  bg1: string;
  bg2: string;
}

const PALETTES: Palette[] = [
  // Blues
  { fg: '#93c5fd', bg1: '#06b6d4', bg2: '#1e3a8a' }, // cyan to deep blue
  { fg: '#7dd3fc', bg1: '#3b82f6', bg2: '#1e40af' }, // sky to blue
  { fg: '#a5f3fc', bg1: '#0ea5e9', bg2: '#0c4a6e' }, // light cyan to dark blue
  // Greens
  { fg: '#86efac', bg1: '#84cc16', bg2: '#065f46' }, // lime to emerald
  { fg: '#bbf7d0', bg1: '#22c55e', bg2: '#14532d' }, // light green to forest
  { fg: '#a7f3d0', bg1: '#10b981', bg2: '#064e3b' }, // mint to dark teal
  // Purples & Violets
  { fg: '#c4b5fd', bg1: '#ec4899', bg2: '#4c1d95' }, // pink to deep purple
  { fg: '#ddd6fe', bg1: '#a855f7', bg2: '#581c87' }, // lavender to purple
  { fg: '#e9d5ff', bg1: '#8b5cf6', bg2: '#3b0764' }, // light purple to dark violet
  // Pinks & Magentas
  { fg: '#f9a8d4', bg1: '#f97316', bg2: '#831843' }, // orange to magenta
  { fg: '#fbcfe8', bg1: '#ec4899', bg2: '#701a75' }, // light pink to fuchsia
  { fg: '#fce7f3', bg1: '#f43f5e', bg2: '#881337' }, // rose to dark pink
  // Oranges & Yellows
  { fg: '#fdba74', bg1: '#fbbf24', bg2: '#b91c1c' }, // yellow to red
  { fg: '#fed7aa', bg1: '#f59e0b', bg2: '#92400e' }, // peach to amber
  { fg: '#fef08a', bg1: '#eab308', bg2: '#713f12' }, // yellow to brown
  // Reds
  { fg: '#fca5a5', bg1: '#f472b6', bg2: '#7f1d1d' }, // pink to crimson
  { fg: '#fecaca', bg1: '#ef4444', bg2: '#7f1d1d' }, // light red to dark red
  // Indigos & Teals
  { fg: '#a5b4fc', bg1: '#22d3ee', bg2: '#164e63' }, // cyan to dark cyan
  { fg: '#5eead4', bg1: '#a3e635', bg2: '#0f766e' }, // lime to teal
  { fg: '#99f6e4', bg1: '#14b8a6', bg2: '#134e4a' }, // light teal to dark teal
  // Warm Browns & Corals
  { fg: '#fcd34d', bg1: '#ea580c', bg2: '#78350f' }, // gold to burnt orange
  { fg: '#fb923c', bg1: '#dc2626', bg2: '#7c2d12' }, // orange to deep red
  // Cool Slates
  { fg: '#cbd5e1', bg1: '#059669', bg2: '#134e4a' }, // slate to emerald
  { fg: '#e2e8f0', bg1: '#0891b2', bg2: '#164e63' }, // light slate to dark cyan
];

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
}

function getPaletteFromHash(hash: number): Palette {
  return PALETTES[hash % PALETTES.length]!;
}

/**
 * Generate a GitHub-style identicon SVG with gradient background
 * Uses pixel-aligned coordinates for crisp rendering
 * SVG handles its own circular clipping - no CSS overflow-hidden needed
 * @param email - User email to hash
 * @param size - Target display size in pixels (default 32)
 * @returns SVG string
 */
export function generateIdenticon(email: string, size: number = 32): string {
  const hash = hashString(email);
  const palette = getPaletteFromHash(hash);

  // 5x5 grid, symmetric so only need 15 values (3 columns × 5 rows)
  const cells: boolean[] = [];
  for (let i = 0; i < 15; i++) {
    cells.push(((hash >> i) & 1) === 1);
  }

  // Scale everything to target size with pixel-aligned coordinates
  // Grid: 5x5 cells centered with ~19% padding on each side
  const viewBoxSize = size;
  const center = size / 2;
  const radius = size / 2;
  const padding = Math.round(size * 0.1875); // ~19% padding
  const gridSize = size - 2 * padding;
  const cellSize = gridSize / 5;

  let rects = '';
  for (let row = 0; row < 5; row++) {
    for (let col = 0; col < 3; col++) {
      const idx = row * 3 + col;
      if (cells[idx]) {
        const x = padding + col * cellSize;
        const y = padding + row * cellSize;
        rects += `<rect x="${x}" y="${y}" width="${cellSize}" height="${cellSize}" fill="${palette.fg}"/>`;
        // Mirror to right side
        if (col < 2) {
          const mirrorX = padding + (4 - col) * cellSize;
          rects += `<rect x="${mirrorX}" y="${y}" width="${cellSize}" height="${cellSize}" fill="${palette.fg}"/>`;
        }
      }
    }
  }

  // Unique IDs using hash + random to avoid conflicts when multiple identicons on page
  const uniqueId = `${hash}-${Math.random().toString(36).slice(2, 8)}`;
  const gradientId = `bg-${uniqueId}`;
  const clipId = `clip-${uniqueId}`;

  const defs = `<defs>
    <linearGradient id="${gradientId}" x1="0%" y1="100%" x2="100%" y2="0%"><stop offset="0%" stop-color="${palette.bg1}"/><stop offset="100%" stop-color="${palette.bg2}"/></linearGradient>
    <clipPath id="${clipId}"><circle cx="${center}" cy="${center}" r="${radius}"/></clipPath>
  </defs>`;

  // Content inside clip path - background and pixels
  const clippedContent = `<g clip-path="url(#${clipId})">
    <circle cx="${center}" cy="${center}" r="${radius}" fill="url(#${gradientId})"/>
    <g shape-rendering="crispEdges">${rects}</g>
  </g>`;

  // Border outside clip path for crisp anti-aliased rendering
  // Scale stroke width proportionally (2px at 80px size)
  const strokeWidth = Math.max(1, size / 40);
  const border = `<circle cx="${center}" cy="${center}" r="${radius - strokeWidth / 2}" fill="none" stroke="rgba(0,0,0,0.15)" stroke-width="${strokeWidth}"/>`;

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${viewBoxSize} ${viewBoxSize}" width="100%" height="100%" style="display:block">${defs}${clippedContent}${border}</svg>`;
}

/**
 * Generate identicon as a data URL for use in img src
 * @param email - User email to hash
 * @param size - Target display size in pixels (default 32)
 * @returns Data URL string
 */
export function generateIdenticonDataUrl(email: string, size: number = 32): string {
  const svg = generateIdenticon(email, size);
  return `data:image/svg+xml;base64,${btoa(svg)}`;
}
